import {
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalContent,
  Button,
  ModalFooter,
  ModalBody,
  Text,
  Box,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';

import { useEffect, useRef, useState } from 'react';
import { MigrationService } from './types';
import { useTranslation } from 'react-i18next';
import { ProvisionApi } from '@Apis';
import {
  AuthenticationInfoForSpodMigrationRes,
  MigrationStatusForSpodMigrationRes,
  OdMigrationAuthInfo,
  SpMigrationAuthInfo,
} from '@Types/provision';
import { StepProps, Steps } from 'antd';
import { ProvisionOrgInfo, StepIcon } from '@Dashboard/ProvisionModal/share';
import CSpinner from '../../../components/CSpinner';
import { deleteCookie, getProvisionCookie } from '../../../utils/common';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import Note from '../../../components/Common/Note';

//先判断有几个需要migration
export const SpodMigrationModal = ({
  orgId,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  onClose,
}: {
  orgId: string;
  onClose: () => void;
}) => {
  const [authInfo, setAuthInfo] =
    useState<AuthenticationInfoForSpodMigrationRes>();
  const [migrationStatus, setMigrationStatus] =
    useState<MigrationStatusForSpodMigrationRes>();

  // migration to which service
  const [migrationIndex, setMigrationIndex] = useState(0);

  const hasNextService = migrationIndex === 0 && authInfo?.length === 2;

  const onNext = () => {
    if (hasNextService) {
      setMigrationIndex(1);
    } else {
      console.log('submit migration, trigger sync data');
      ProvisionApi.apiSubmitSpodMigration(orgId);
      onClose();
    }
  };

  useEffect(() => {
    (async () => {
      const [infoRes, statusRes] = await Promise.all([
        ProvisionApi.apiGetSpodMigrationAuthenticationInfo(orgId),
        ProvisionApi.apiGetSpodMigrationStatus(orgId),
      ]);
      setAuthInfo(infoRes.data);
      setMigrationStatus(statusRes.data);
    })();
  }, [orgId]);

  if (!authInfo || !migrationStatus) return <></>;

  const currentAuthInfo = authInfo[migrationIndex];

  // status: 'created' | 'failure'
  const currentMigrationStatus = migrationStatus.authorization?.apps.find(
    (app) => app.app === currentAuthInfo.app
  ) || { status: 'failure' };

  return (
    <SingleModal
      orgId={orgId}
      key={currentAuthInfo.app}
      service={currentAuthInfo.app}
      authInfo={currentAuthInfo}
      onClose={onClose}
      onNext={onNext}
      hasNextService={hasNextService}
      isTokenCreated={currentMigrationStatus.status === 'created'}
    />
  );
};

enum StepStatus {
  NotStarted = 0,
  PermissionGranted = 1,
}

export const SingleModal = ({
  orgId,
  service,
  authInfo,
  onClose,
  onNext,
  hasNextService,
  isTokenCreated,
}: {
  orgId: string;
  service: MigrationService;
  authInfo: SpMigrationAuthInfo | OdMigrationAuthInfo;
  onClose: () => void;
  onNext: () => void;
  hasNextService: boolean;
  isTokenCreated: boolean;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const [stepStatus, setStepStatus] = useState<StepStatus>(
    isTokenCreated ? StepStatus.PermissionGranted : StepStatus.NotStarted
  );

  const timeRef = useRef<any>(null);
  const [loading, toggleLoading] = useToggle(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  const serviceKey = (
    {
      [MigrationService.SharePoint]: 'spMigrate',
      [MigrationService.OneDrive]: 'odMigrate',
    } as const
  )[service];

  // const titleTextStyle = {
  //   fontSize: '16px',
  //   lineHeight: '22px',
  //   fontWeight: '400',
  //   color: colorStyle.color.primary,
  // };

  const onGrantPermission = async () => {
    const cookieName = (
      {
        [MigrationService.SharePoint]: 'provision_status_azuread_graph_sp',
        [MigrationService.OneDrive]: 'provision_status_azuread_graph_od',
      } as const
    )[service];

    const authUrl = authInfo?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    let isFirst = true;
    timeRef.current = setInterval(async () => {
      if (!isFirst) return;
      const provisionInfo = getProvisionCookie(cookieName);
      if (provisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        isFirst = false;
        const { error, code } = JSON.parse(provisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveSpodMigrationInfo(
              service,
              code,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision:provision.service.${service}.error.saveInfo.${res.data?.reason}`,
                  'common:common.apiException.internalError',
                ])
              );
            } else {
              setStepStatus(StepStatus.PermissionGranted);
            }
          } catch {
            setErrorMsg(t('common:common.apiException.internalError'));
          }
        } else {
          setErrorMsg(
            t(`provision:provision.service.${service}.error.deniedGrant`)
          );
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  const stepItems: StepProps[] = [
    {
      icon: <StepIcon checked={stepStatus > StepStatus.NotStarted} />,
      title: t(
        `administration:administration.sa.table.status.${serviceKey}.grantPermission`
      ),
      description: (
        <Box>
          {stepStatus > StepStatus.NotStarted ? (
            <Text color={colorStyle.color.tertiary} pt="8px" lineHeight="20px">
              {t('provision:provision.common.permissionGranted')}
            </Text>
          ) : (
            <Button
              variant="secondary"
              borderRadius="2rem"
              onClick={onGrantPermission}
            >
              {t('provision:provision.button.grantPermission')}
            </Button>
          )}
        </Box>
      ),
    },
    // {
    //   icon: <StepIcon checked={stepStatus > StepStatus.WaitConfirm} />,
    //   title: (
    //     <Text {...titleTextStyle}>
    //       <Trans
    //         i18nKey={`administration.sa.table.status.${serviceKey}.instructions`}
    //         ns="administration"
    //       >
    //         placeholder
    //         <Link
    //           href={getCasOLHLink(
    //             CAS_DOC_LINK.administrator.migration[serviceKey],
    //             i18next.language
    //           )}
    //           target="_blank"
    //         >
    //           placeholder
    //         </Link>
    //         placeholder
    //       </Trans>
    //     </Text>
    //   ),
    //   description: (
    //     <Stack pt="8px" gap="16px">
    //       {stepStatus > StepStatus.NotStarted && (
    //         <Alert mt="8px" variant="outline" severity="info">
    //           {t('provision:provision.common.assignAppIdTip', {
    //             appId: authInfo?.appId || '',
    //           })}
    //         </Alert>
    //       )}
    //       <Box>
    //         {stepStatus > StepStatus.WaitConfirm ? (
    //           <Text
    //             color={colorStyle.color.tertiary}
    //             pt="8px"
    //             lineHeight="20px"
    //           >
    //             {t('provision:provision.common.permissionGranted')}
    //           </Text>
    //         ) : (
    //           <ConfirmPopover
    //             stepStatus={stepStatus}
    //             service={serviceKey}
    //             onConfirm={() => {
    //               setStepStatus(StepStatus.ConfirmDone);
    //             }}
    //           />
    //         )}
    //       </Box>
    //     </Stack>
    //   ),
    // },
  ];

  return (
    <>
      {loading && <CSpinner />}
      <Modal
        ensureFocus={true}
        autoFocus={false}
        closeOnEsc={true}
        closeOnOutsideClick={false}
        isClosable={true}
        isOpen={true}
        onClose={onClose}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(
              'administration:administration.sa.table.status.spodMigrate.header'
            )}
          </ModalHeader>
          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />
            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Steps
              current={stepStatus}
              items={stepItems}
              direction="vertical"
            />
            <Note
              content={t(
                `administration:administration.sa.table.status.${serviceKey}.note`
              )}
            />
          </ModalBody>
          <ModalFooter columnGap="2x">
            <Button
              variant="primary"
              minWidth="20x"
              onClick={onNext}
              disabled={stepStatus < StepStatus.PermissionGranted}
            >
              {hasNextService
                ? t('common:common.action.label.next')
                : t('common:common.action.label.submit')}
            </Button>
            <Button minWidth="20x" onClick={onClose}>
              {t('common:common.action.label.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

// const ConfirmPopover = ({
//   stepStatus,
//   onConfirm,
//   service,
// }: {
//   stepStatus: StepStatus;
//   onConfirm: () => void;
//   service: 'spMigrate' | 'odMigrate';
// }) => {
//   const { t } = useTranslation(['administration', 'common']);
//   return (
//     <Popover>
//       {({ onClose }: any) => (
//         <>
//           <PopoverTrigger>
//             <Button
//               variant="secondary"
//               borderRadius="2rem"
//               disabled={stepStatus < StepStatus.PermissionGranted}
//             >
//               {t('provision:provision.button.confirmPermissionGrant')}
//             </Button>
//           </PopoverTrigger>
//           <PopoverContent PopperProps={{ usePortal: true }} maxWidth={480}>
//             <PopoverBody>
//               <Text>
//                 {t(
//                   `administration:administration.sa.table.status.${service}.confirmDesc`
//                 )}
//               </Text>
//               <Text>
//                 {t(
//                   `administration:administration.sa.table.status.${service}.confirmWarningDesc`
//                 )}
//               </Text>
//               <Stack pl="12px">
//                 <Text>
//                   {t(
//                     `administration:administration.sa.table.status.${service}.confirmWarning1`
//                   )}
//                 </Text>
//                 <Text>
//                   {t(
//                     `administration:administration.sa.table.status.${service}.confirmWarning2`
//                   )}
//                 </Text>
//               </Stack>
//             </PopoverBody>
//             <PopoverFooter>
//               <Flex justifyContent="flex-end" columnGap="4x">
//                 <Button
//                   variant="primary"
//                   onClick={() => {
//                     onConfirm();
//                     onClose();
//                   }}
//                 >
//                   {t('common.action.label.yes')}
//                 </Button>
//                 <Button variant="default" onClick={onClose}>
//                   {t('common.action.label.no')}
//                 </Button>
//               </Flex>
//             </PopoverFooter>
//           </PopoverContent>
//         </>
//       )}
//     </Popover>
//   );
// };
